// Meta (Facebook) Pixel — lazy loader, gated by RGPD consent.
// Loads the pixel script ONLY after the user grants analytics_storage consent.
// Exposes `initMetaPixel()` and `trackPixel(event, params)`.
//
// Standard events used by this project (per the user's tracking spec):
//   - `Lead`     → fired when an appointment form is submitted successfully.
//   - `Contact`  → fired when the user clicks the WhatsApp CTA.
//
// We intentionally DO NOT auto-track PageView on every navigation: it's fired
// once on init (after consent) and then route changes go through GA4 only.

import { readConsent } from "./consent";

let loaded = false;
let booted = false;

function ensureFbq() {
  if (window.fbq) return;
  // Standard Facebook Pixel bootstrap (snippet adapted to be ESLint-friendly).
  const n = function (...args) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  };
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;

  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
}

/** Boot the Meta Pixel. Idempotent. Requires analytics consent. */
export function initMetaPixel() {
  if (booted) return;
  const id = process.env.REACT_APP_META_PIXEL_ID;
  if (!id) return;
  const consent = readConsent();
  if (!consent || !consent.analytics) return; // wait for consent
  ensureFbq();
  if (!loaded) {
    window.fbq("init", id);
    loaded = true;
  }
  window.fbq("track", "PageView");
  booted = true;
}

/** Fire a Meta Pixel standard or custom event. Silent no-op if not booted. */
export function trackPixel(event, params = {}) {
  if (!booted || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", event, params);
  } catch { /* noop */ }
}

/** Subscribe once to consent updates so the pixel boots when the user accepts. */
export function bindMetaPixelToConsent() {
  const onUpdate = () => {
    const c = readConsent();
    if (c && c.analytics) initMetaPixel();
  };
  window.addEventListener("icb:consent-update", onUpdate);
  // If consent is already granted at load time, boot immediately.
  onUpdate();
}
